import React, { useState, useEffect } from 'react';
import { Card, Form, TimePicker, Switch, Button, Select, Space, Typography, message, Spin, Table, Tag, Modal, Input, DatePicker } from 'antd';
import { PlusOutlined, DeleteOutlined, CalendarOutlined } from '@ant-design/icons';
import { scheduleApi, TechnicianSchedule, ScheduleException } from '../../services/scheduleApi';
import { apiRoot } from '../../services/apiInstances';
import dayjs, { Dayjs } from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select; // Still needed for exception type Select

interface User {
  id: number;
  first_name: string;
  last_name: string;
  role: string;
  status: string;
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

export const TechnicianScheduleSettings: React.FC = () => {
  const [technicians, setTechnicians] = useState<User[]>([]);
  const [selectedTechnicianId, setSelectedTechnicianId] = useState<number | null>(null);
  const [schedules, setSchedules] = useState<TechnicianSchedule[]>([]);
  const [exceptions, setExceptions] = useState<ScheduleException[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exceptionModalVisible, setExceptionModalVisible] = useState(false);
  const [exceptionForm] = Form.useForm();

  useEffect(() => {
    loadTechnicians();
  }, []);

  useEffect(() => {
    if (selectedTechnicianId) {
      loadTechnicianSchedule();
      loadTechnicianExceptions();
    } else {
      // Reset schedules when no technician is selected
      setSchedules([]);
    }
  }, [selectedTechnicianId]);

  const loadTechnicians = async () => {
    try {
      // Fetch both technicians and owners
      const [techniciansResponse, ownersResponse] = await Promise.all([
        apiRoot.get('/users', {
          params: { role: 'technician', status: 'active' }
        }),
        apiRoot.get('/users', {
          params: { role: 'owner', status: 'active' }
        })
      ]);

      // Combine and sort by name
      const allUsers = [...techniciansResponse.data, ...ownersResponse.data].sort((a, b) => {
        const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
        const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setTechnicians(allUsers);
      if (allUsers.length > 0) {
        setSelectedTechnicianId(allUsers[0].id);
      }
    } catch (error) {
      console.error('[TechnicianSchedule] Error loading technicians:', error);
      message.error('Failed to load technicians and owners');
    }
  };

  const loadTechnicianSchedule = async () => {
    if (!selectedTechnicianId) return;

    setLoading(true);
    try {
      const data = await scheduleApi.getTechnicianSchedule(selectedTechnicianId);

      // Fill in missing days with default inactive schedules
      const fullSchedule = DAYS_OF_WEEK.map(day => {
        const existing = data.find(s => s.day_of_week === day.value);
        return existing || {
          user_id: selectedTechnicianId,
          day_of_week: day.value,
          start_time: '08:00',
          end_time: '17:00',
          is_active: false
        };
      });

      setSchedules(fullSchedule);
    } catch (error) {
      console.error('Error loading schedule:', error);
      message.error('Failed to load schedule');

      // Even on error, initialize with default schedules
      const defaultSchedule = DAYS_OF_WEEK.map(day => ({
        user_id: selectedTechnicianId,
        day_of_week: day.value,
        start_time: '08:00',
        end_time: '17:00',
        is_active: false
      }));
      setSchedules(defaultSchedule);
    } finally {
      setLoading(false);
    }
  };

  const loadTechnicianExceptions = async () => {
    if (!selectedTechnicianId) return;

    try {
      const data = await scheduleApi.getExceptions({ user_id: selectedTechnicianId });
      setExceptions(data);
    } catch (error) {
      console.error('Error loading exceptions:', error);
    }
  };

  const handleScheduleChange = (dayOfWeek: number, field: string, value: any) => {
    setSchedules(prev => prev.map(schedule => {
      if (schedule.day_of_week === dayOfWeek) {
        return { ...schedule, [field]: value };
      }
      return schedule;
    }));
  };

  const handleSaveSchedule = async () => {
    if (!selectedTechnicianId) return;

    setSaving(true);
    try {
      const scheduleData = schedules.map(({ user_id, ...rest }) => rest);
      await scheduleApi.updateTechnicianSchedule(selectedTechnicianId, scheduleData);
      message.success('Schedule saved successfully');
    } catch (error) {
      console.error('Error saving schedule:', error);
      message.error('Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleAddException = async (values: any) => {
    if (!selectedTechnicianId) return;

    try {
      await scheduleApi.createException({
        user_id: selectedTechnicianId,
        exception_type: values.exception_type,
        start_datetime: values.dateRange[0].toISOString(),
        end_datetime: values.dateRange[1].toISOString(),
        reason: values.reason,
        is_recurring: false
      });
      message.success('Exception added successfully');
      setExceptionModalVisible(false);
      exceptionForm.resetFields();
      loadTechnicianExceptions();
    } catch (error) {
      console.error('Error adding exception:', error);
      message.error('Failed to add exception');
    }
  };

  const handleDeleteException = async (id: number) => {
    try {
      await scheduleApi.deleteException(id);
      message.success('Exception deleted successfully');
      loadTechnicianExceptions();
    } catch (error) {
      console.error('Error deleting exception:', error);
      message.error('Failed to delete exception');
    }
  };

  const exceptionColumns = [
    {
      title: 'Type',
      dataIndex: 'exception_type',
      key: 'exception_type',
      render: (type: string) => (
        <Tag color={
          type === 'time_off' ? 'blue' :
          type === 'holiday' ? 'green' :
          type === 'break' ? 'orange' : 'default'
        }>
          {type.replace('_', ' ').toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Start',
      dataIndex: 'start_datetime',
      key: 'start_datetime',
      render: (date: string) => dayjs(date).format('MMM DD, YYYY hh:mm A')
    },
    {
      title: 'End',
      dataIndex: 'end_datetime',
      key: 'end_datetime',
      render: (date: string) => dayjs(date).format('MMM DD, YYYY hh:mm A')
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason'
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: ScheduleException) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDeleteException(record.id!)}
        >
          Delete
        </Button>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Employee Schedule Management</Title>
      <Text type="secondary">Set regular work hours and time off for technicians and owners</Text>

      <Card style={{ marginTop: '24px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Technician Selector */}
          <div>
            <Text strong>Select Employee:</Text>
            <Select
              style={{ width: '350px', marginLeft: '16px' }}
              value={selectedTechnicianId}
              onChange={setSelectedTechnicianId}
              placeholder="Select an employee"
              showSearch
              filterOption={(input, option) => {
                const label = `${option?.label || ''}`.toLowerCase();
                return label.includes(input.toLowerCase());
              }}
              options={technicians.map(tech => ({
                value: tech.id,
                label: `${tech.first_name} ${tech.last_name} (${tech.role === 'owner' ? 'Owner' : 'Technician'})`
              }))}
            />
          </div>

          {/* Weekly Schedule */}
          {selectedTechnicianId && (
            <Spin spinning={loading}>
              <Title level={4}>Weekly Schedule</Title>
              <div style={{ marginTop: '16px' }}>
                {DAYS_OF_WEEK.map(day => {
                  const schedule = schedules.find(s => s.day_of_week === day.value);
                  if (!schedule) return null;

                  return (
                    <div
                      key={day.value}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '16px',
                        padding: '12px',
                        backgroundColor: schedule.is_active ? '#f0f5ff' : '#fafafa',
                        borderRadius: '4px'
                      }}
                    >
                      <div style={{ width: '120px', fontWeight: 'bold' }}>
                        {day.label}
                      </div>
                      <Switch
                        checked={schedule.is_active}
                        onChange={(checked) => handleScheduleChange(day.value, 'is_active', checked)}
                        style={{ marginRight: '16px' }}
                      />
                      <TimePicker
                        format="HH:mm"
                        value={dayjs(schedule.start_time, 'HH:mm')}
                        onChange={(time) => handleScheduleChange(day.value, 'start_time', time?.format('HH:mm') || '08:00')}
                        disabled={!schedule.is_active}
                        style={{ marginRight: '8px' }}
                      />
                      <Text style={{ margin: '0 8px' }}>to</Text>
                      <TimePicker
                        format="HH:mm"
                        value={dayjs(schedule.end_time, 'HH:mm')}
                        onChange={(time) => handleScheduleChange(day.value, 'end_time', time?.format('HH:mm') || '17:00')}
                        disabled={!schedule.is_active}
                      />
                    </div>
                  );
                })}
              </div>

              <Button
                type="primary"
                onClick={handleSaveSchedule}
                loading={saving}
                size="large"
                style={{ marginTop: '16px' }}
              >
                Save Schedule
              </Button>
            </Spin>
          )}

          {/* Exceptions (Time Off) */}
          {selectedTechnicianId && (
            <>
              <div style={{ marginTop: '32px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Title level={4}>Schedule Exceptions</Title>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setExceptionModalVisible(true)}
                  >
                    Add Exception
                  </Button>
                </div>
                <Table
                  columns={exceptionColumns}
                  dataSource={exceptions}
                  rowKey="id"
                  style={{ marginTop: '16px' }}
                  scroll={{ x: 'max-content' }}
                  pagination={{ pageSize: 10 }}
                />
              </div>

              {/* Exception Modal */}
              <Modal
                title="Add Schedule Exception"
                open={exceptionModalVisible}
                onCancel={() => {
                  setExceptionModalVisible(false);
                  exceptionForm.resetFields();
                }}
                footer={null}
              >
                <Form
                  form={exceptionForm}
                  layout="vertical"
                  onFinish={handleAddException}
                >
                  <Form.Item
                    label="Exception Type"
                    name="exception_type"
                    rules={[{ required: true, message: 'Please select exception type' }]}
                  >
                    <Select placeholder="Select type">
                      <Option value="time_off">Time Off</Option>
                      <Option value="holiday">Holiday</Option>
                      <Option value="break">Break</Option>
                      <Option value="special_hours">Special Hours</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label="Date Range"
                    name="dateRange"
                    rules={[{ required: true, message: 'Please select date range' }]}
                  >
                    <DatePicker.RangePicker
                      showTime
                      format="YYYY-MM-DD HH:mm"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Reason"
                    name="reason"
                  >
                    <Input.TextArea rows={3} placeholder="Optional reason" />
                  </Form.Item>

                  <Form.Item>
                    <Space>
                      <Button type="primary" htmlType="submit">
                        Add Exception
                      </Button>
                      <Button onClick={() => {
                        setExceptionModalVisible(false);
                        exceptionForm.resetFields();
                      }}>
                        Cancel
                      </Button>
                    </Space>
                  </Form.Item>
                </Form>
              </Modal>
            </>
          )}
        </Space>
      </Card>
    </div>
  );
};
